import { z } from 'zod';
import { mutationWithAuth, queryWithAuth } from '../graphql-helpers';
import {
  GET_LITTER,
  GET_LITTERS,
  ADD_LITTER,
  EDIT_LITTER,
  DELETE_LITTER,
} from '../graphql/litters';

const DogRefSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  longName: z.string().optional(),
});

export const LitterSchema = z
  .object({
    id: z.string(),
    name: z.string().optional(),
    litterType: z.enum(['planned', 'upcoming', 'past']).optional(),
    sire: DogRefSchema.nullable().optional(),
    dam: DogRefSchema.nullable().optional(),
    sireId: z.string().optional(),
    damId: z.string().optional(),
    expectedMatingDate: z.string().optional(),
    expectedDueMonth: z.string().optional(),
    expectedDueDate: z.string().optional(),
    expectedPuppyDate: z.string().optional(),
    dobOfPuppies: z.string().optional(),
    limitedRegistrationPrice: z.number().optional(),
    mainRegistrationPrice: z.number().optional(),
    expectedColors: z.array(z.string()).optional(),
    content: z.string().optional(),
    imagesUrls: z.array(z.string()).optional(),
  })
  .passthrough();

export type Litter = z.infer<typeof LitterSchema> & {
  id: string;
};

export const LittersSchema = z.array(LitterSchema);

export async function listLitters(
  litterType?: string,
  getAccessTokenFn?: () => Promise<string | null>,
) {
  const filter = litterType ? { litterType } : undefined;

  const response = await queryWithAuth<{ getLitters: Array<z.infer<typeof LitterSchema>> }>(
    GET_LITTERS,
    filter ? { filter } : undefined,
    getAccessTokenFn,
    false,
  );

  const litters = response.getLitters;
  return litters as Litter[];
}

export async function getLitter(id: string, getAccessTokenFn?: () => Promise<string | null>) {
  const response = await queryWithAuth<{ getLitter: z.infer<typeof LitterSchema> }>(
    GET_LITTER,
    { id },
    getAccessTokenFn,
    false,
  );

  const litter = response.getLitter;
  return litter as Litter;
}

export async function createLitter(
  input: Partial<Litter>,
  getAccessTokenFn?: () => Promise<string | null>,
) {
  const litterInput = {
    name: input.name,
    litterType: input.litterType || '',
    expectedMatingDate: input.expectedMatingDate,
    expectedDueMonth: input.expectedDueMonth,
    expectedDueDate: input.expectedDueDate,
    expectedPuppyDate: input.expectedPuppyDate,
    dobOfPuppies: input.dobOfPuppies,
    sireId: input.sireId,
    damId: input.damId,
    numberOfPuppies: input.numberOfPuppies,
    availablePuppies: input.availablePuppies,
    limitedRegistrationPrice: input.limitedRegistrationPrice,
    mainRegistrationPrice: input.mainRegistrationPrice,
    expectedColors: input.expectedColors || [],
    content: input.content,
    coverImgUrl: input.coverImgUrl,
    imagesUrls: input.imagesUrls || [],
  };

  const response = await mutationWithAuth<{ addLitter: z.infer<typeof LitterSchema> }>(
    ADD_LITTER,
    { input: litterInput },
    getAccessTokenFn,
    true, // Requires authentication
  );

  const litter = response.addLitter;
  return litter as Litter;
}

export async function updateLitter(
  id: string,
  input: Partial<Litter>,
  getAccessTokenFn?: () => Promise<string | null>,
) {
  const litterInput = {
    name: input.name,
    litterType: input.litterType || '',
    expectedMatingDate: input.expectedMatingDate,
    expectedDueMonth: input.expectedDueMonth,
    expectedDueDate: input.expectedDueDate,
    expectedPuppyDate: input.expectedPuppyDate,
    dobOfPuppies: input.dobOfPuppies,
    sireId: input.sireId,
    damId: input.damId,
    numberOfPuppies: input.numberOfPuppies,
    availablePuppies: input.availablePuppies,
    limitedRegistrationPrice: input.limitedRegistrationPrice,
    mainRegistrationPrice: input.mainRegistrationPrice,
    expectedColors: input.expectedColors || [],
    content: input.content,
    coverImgUrl: input.coverImgUrl,
    imagesUrls: input.imagesUrls || [],
  };

  const response = await mutationWithAuth<{ editLitter: z.infer<typeof LitterSchema> }>(
    EDIT_LITTER,
    { id, input: litterInput },
    getAccessTokenFn,
    true, // Requires authentication
  );

  const litter = response.editLitter;
  return litter as Litter;
}

export async function deleteLitter(id: string, getAccessTokenFn?: () => Promise<string | null>) {
  const response = await mutationWithAuth<{ deleteLitter: boolean }>(
    DELETE_LITTER,
    { id },
    getAccessTokenFn,
    true, // Requires authentication
  );

  return { success: response.deleteLitter };
}
